import { differenceInDays, parseISO, startOfDay } from 'date-fns';

/**
 * DeadlineIntelligenceService
 *
 * Critical service for deadline-aware task prioritization and workload planning.
 * Implements the UX philosophy principle: "Contextually Intelligent"
 *
 * Key Responsibilities:
 * - Calculate urgency phases based on deadline proximity
 * - Generate realistic workload scenarios
 * - Prioritize tasks by deadline criticality
 * - Provide daily sprint breakdowns
 */

class DeadlineIntelligenceService {
  /**
   * Phase thresholds (in days before deadline)
   */
  static PHASES = {
    NORMAL: 'NORMAL',           // 30+ days
    CRUNCH_30: 'CRUNCH_30',     // 14-30 days
    SPRINT_14: 'SPRINT_14',     // 7-14 days
    EMERGENCY_7: 'EMERGENCY_7'  // < 7 days
  };

  /**
   * Default time estimates per task type (in hours)
   */
  static DEFAULT_TIME_ESTIMATES = {
    essay_supplemental: 2.5,
    essay_personal_statement: 4,
    essay_why_school: 2,
    common_app_activities: 3,
    common_app_honors: 1,
    financial_aid_fafsa: 3,
    financial_aid_css: 3,
    recommendation_request: 0.5,
    recommendation_followup: 0.25,
    application_review: 2,
    application_submit: 1,
    default: 1.5
  };

  /**
   * Get nearest application deadline across all colleges
   * Priority order: ED first, then earliest deadline
   * @param {Array} colleges - Array of college objects with deadlines
   * @returns {Object|null} - { college, deadline, daysUntil, round, phase }
   */
  static getNearestDeadline(colleges) {
    if (!colleges || colleges.length === 0) {
      console.log('[DeadlineIntelligence] No colleges provided');
      return null;
    }

    const today = startOfDay(new Date());
    let nearest = null;
    let minDays = Infinity;
    let edDeadline = null; // Track ED separately for priority

    console.log('[DeadlineIntelligence] Checking deadlines for', colleges.length, 'colleges');

    colleges.forEach(college => {
      const deadlines = college.deadlines || college;
      const applicationRound = college.application_round;

      console.log('[DeadlineIntelligence]', college.name || college.college_name, 'application_round:', applicationRound);

      // CRITICAL FIX: Only check the deadline that matches the student's selected application round
      let relevantDeadline = null;
      let roundType = null;

      switch(applicationRound) {
        case 'early_decision':
        case 'early_decision_1':
          relevantDeadline = deadlines.ed || deadlines.early_decision;
          roundType = 'early_decision';
          break;
        case 'early_decision_2':
          relevantDeadline = deadlines.ed2 || deadlines.early_decision_2;
          roundType = 'early_decision_2';
          break;
        case 'early_action':
          relevantDeadline = deadlines.ea || deadlines.early_action;
          roundType = 'early_action';
          break;
        case 'regular_decision':
        case 'regular':
        default:
          relevantDeadline = deadlines.rd || deadlines.regular_decision;
          roundType = 'regular_decision';
          break;
      }

      console.log('[DeadlineIntelligence]', college.name || college.college_name, 'relevant deadline for', applicationRound, ':', relevantDeadline);

      if (!relevantDeadline) return;

      const deadlineDate = typeof relevantDeadline === 'string'
        ? parseISO(relevantDeadline)
        : relevantDeadline;

      const daysUntil = differenceInDays(deadlineDate, today);

      // Only consider future or very recent deadlines
      if (daysUntil >= -2) {
        const deadlineInfo = {
          college,
          college_id: college.id,
          college_name: college.name || college.college_name,
          deadline: deadlineDate,
          daysUntil,
          round: roundType,
          phase: this.determinePhase(daysUntil)
        };

        // PRIORITY LOGIC: ED always takes priority over EA/RD
        if (roundType === 'early_decision' || roundType === 'early_decision_2') {
          // If we haven't found an ED yet, or this ED is sooner
          if (!edDeadline || daysUntil < edDeadline.daysUntil) {
            edDeadline = deadlineInfo;
          }
        }

        // Track overall nearest deadline (for EA/RD comparison)
        if (daysUntil < minDays) {
          minDays = daysUntil;
          nearest = deadlineInfo;
        }
      }
    });

    // RETURN PRIORITY: ED first, then nearest
    if (edDeadline) {
      console.log('[DeadlineIntelligence] Prioritizing ED deadline:', edDeadline.college_name, edDeadline.daysUntil, 'days');
      return edDeadline;
    }

    console.log('[DeadlineIntelligence] No ED deadline, using nearest:', nearest?.college_name, nearest?.daysUntil, 'days');
    return nearest;
  }

  /**
   * Determine urgency phase based on days until deadline
   * @param {number} daysUntil - Days until deadline
   * @returns {string} - Phase constant
   */
  static determinePhase(daysUntil) {
    if (daysUntil < 7) return this.PHASES.EMERGENCY_7;
    if (daysUntil < 14) return this.PHASES.SPRINT_14;
    if (daysUntil < 30) return this.PHASES.CRUNCH_30;
    return this.PHASES.NORMAL;
  }

  /**
   * Get current phase across all colleges
   * @param {Array} colleges - Array of college objects
   * @returns {Object} - { phase, deadline, college }
   */
  static getCurrentPhase(colleges) {
    const nearest = this.getNearestDeadline(colleges);

    if (!nearest) {
      return {
        phase: this.PHASES.NORMAL,
        deadline: null,
        college: null
      };
    }

    return {
      phase: nearest.phase,
      deadline: nearest.deadline,
      daysUntil: nearest.daysUntil,
      college: nearest.college_name,
      college_id: nearest.college_id,
      round: nearest.round
    };
  }

  /**
   * Calculate total workload hours for a specific college application
   * @param {string} collegeId - College ID
   * @param {Array} tasks - All tasks
   * @returns {Object} - { total, breakdown: { essays, forms, reviews, etc } }
   */
  static calculateWorkloadPerCollege(collegeId, tasks) {
    const collegeTasks = tasks.filter(task =>
      task.college_id === collegeId ||
      task.metadata?.college_id === collegeId
    );

    const breakdown = {
      essays: 0,
      forms: 0,
      financial_aid: 0,
      recommendations: 0,
      review: 0,
      other: 0
    };

    let total = 0;

    collegeTasks.forEach(task => {
      const hours = this.estimateTaskHours(task);
      total += hours;

      // Categorize
      const category = task.category || task.metadata?.category || 'other';
      if (category.includes('essay')) {
        breakdown.essays += hours;
      } else if (category.includes('financial') || category.includes('fafsa') || category.includes('css')) {
        breakdown.financial_aid += hours;
      } else if (category.includes('recommendation') || category.includes('rec')) {
        breakdown.recommendations += hours;
      } else if (category.includes('review') || category.includes('submit')) {
        breakdown.review += hours;
      } else if (category.includes('form') || category.includes('application')) {
        breakdown.forms += hours;
      } else {
        breakdown.other += hours;
      }
    });

    return { total, breakdown };
  }

  /**
   * Estimate hours for a single task
   * @param {Object} task - Task object
   * @returns {number} - Estimated hours
   */
  static estimateTaskHours(task) {
    // Check if task has explicit time estimate
    if (task.estimated_hours) return task.estimated_hours;
    if (task.time_estimate) return task.time_estimate;

    // Try to infer from task type or category
    const category = task.category || task.metadata?.category || '';
    const title = (task.title || '').toLowerCase();

    // Match against known patterns
    for (const [type, hours] of Object.entries(this.DEFAULT_TIME_ESTIMATES)) {
      if (category.includes(type) || title.includes(type.replace('_', ' '))) {
        return hours;
      }
    }

    return this.DEFAULT_TIME_ESTIMATES.default;
  }

  /**
   * Generate workload scenarios for multiple college combinations
   * @param {Array} colleges - Array of colleges with deadlines
   * @param {Array} tasks - All tasks
   * @param {Date} currentDate - Current date (for testing)
   * @returns {Array} - Array of scenario objects
   */
  static generateScenarios(colleges, tasks, currentDate = new Date()) {
    if (!colleges || colleges.length === 0) return [];

    const nearest = this.getNearestDeadline(colleges);
    if (!nearest) return [];

    const daysUntil = nearest.daysUntil;
    const scenarios = [];

    console.log('[DeadlineIntelligence] Nearest deadline college:', nearest.college_name, 'Round:', nearest.round);

    // Group colleges by round
    const edSchools = colleges.filter(c =>
      c.application_round === 'early_decision' ||
      c.application_round === 'early_decision_1'
    );
    const eaSchools = colleges.filter(c =>
      c.application_round === 'early_action'
    );

    console.log('[DeadlineIntelligence] ED Schools:', edSchools.map(c => c.name));
    console.log('[DeadlineIntelligence] EA Schools:', eaSchools.map(c => c.name));

    // Scenario 1: ED Only (if ED exists)
    if (edSchools.length > 0) {
      const edSchool = edSchools[0];
      const workload = this.calculateWorkloadPerCollege(edSchool.id, tasks);
      const dailyHours = daysUntil > 0 ? workload.total / daysUntil : workload.total;

      scenarios.push({
        name: `ED Only (${edSchool.name || edSchool.college_name})`,
        schools: [edSchool],
        totalHours: workload.total,
        dailyHours: Math.round(dailyHours * 10) / 10,
        daysAvailable: daysUntil,
        feasible: dailyHours <= 5,
        status: dailyHours <= 4 ? 'GOOD' : dailyHours <= 6 ? 'TIGHT' : 'UNREALISTIC',
        breakdown: workload.breakdown,
        recommendation: dailyHours <= 4
          ? '✓ Realistic with buffer time'
          : dailyHours <= 6
            ? '⚠️ Requires full commitment'
            : '🚨 Not recommended - quality will suffer'
      });
    }

    // Scenario 2: ED + 1 EA (if both exist)
    if (edSchools.length > 0 && eaSchools.length > 0) {
      const edSchool = edSchools[0];
      const eaSchool = eaSchools[0];

      const edWorkload = this.calculateWorkloadPerCollege(edSchool.id, tasks);
      const eaWorkload = this.calculateWorkloadPerCollege(eaSchool.id, tasks);
      const totalHours = edWorkload.total + eaWorkload.total;
      const dailyHours = daysUntil > 0 ? totalHours / daysUntil : totalHours;

      scenarios.push({
        name: `ED + 1 EA (${edSchool.name || edSchool.college_name} + ${eaSchool.name || eaSchool.college_name})`,
        schools: [edSchool, eaSchool],
        totalHours,
        dailyHours: Math.round(dailyHours * 10) / 10,
        daysAvailable: daysUntil,
        feasible: dailyHours <= 7,
        status: dailyHours <= 6 ? 'GOOD' : dailyHours <= 8 ? 'TIGHT' : 'UNREALISTIC',
        breakdown: {
          essays: edWorkload.breakdown.essays + eaWorkload.breakdown.essays,
          forms: edWorkload.breakdown.forms + eaWorkload.breakdown.forms,
          financial_aid: edWorkload.breakdown.financial_aid + eaWorkload.breakdown.financial_aid,
          recommendations: edWorkload.breakdown.recommendations + eaWorkload.breakdown.recommendations,
          review: edWorkload.breakdown.review + eaWorkload.breakdown.review,
          other: edWorkload.breakdown.other + eaWorkload.breakdown.other
        },
        recommendation: dailyHours <= 6
          ? '✓ Doable if focused'
          : dailyHours <= 8
            ? '⚠️ Very tight - little room for delays'
            : '🚨 Not recommended - defer one school'
      });
    }

    // Scenario 3: ED + 2 EA (if all exist)
    if (edSchools.length > 0 && eaSchools.length >= 2) {
      const edSchool = edSchools[0];
      const eaSchool1 = eaSchools[0];
      const eaSchool2 = eaSchools[1];

      const edWorkload = this.calculateWorkloadPerCollege(edSchool.id, tasks);
      const ea1Workload = this.calculateWorkloadPerCollege(eaSchool1.id, tasks);
      const ea2Workload = this.calculateWorkloadPerCollege(eaSchool2.id, tasks);
      const totalHours = edWorkload.total + ea1Workload.total + ea2Workload.total;
      const dailyHours = daysUntil > 0 ? totalHours / daysUntil : totalHours;

      scenarios.push({
        name: `ED + 2 EA (+ ${eaSchool2.name || eaSchool2.college_name})`,
        schools: [edSchool, eaSchool1, eaSchool2],
        totalHours,
        dailyHours: Math.round(dailyHours * 10) / 10,
        daysAvailable: daysUntil,
        feasible: false,
        status: 'UNREALISTIC',
        breakdown: {
          essays: edWorkload.breakdown.essays + ea1Workload.breakdown.essays + ea2Workload.breakdown.essays,
          forms: edWorkload.breakdown.forms + ea1Workload.breakdown.forms + ea2Workload.breakdown.forms,
          financial_aid: edWorkload.breakdown.financial_aid + ea1Workload.breakdown.financial_aid + ea2Workload.breakdown.financial_aid,
          recommendations: edWorkload.breakdown.recommendations + ea1Workload.breakdown.recommendations + ea2Workload.breakdown.recommendations,
          review: edWorkload.breakdown.review + ea1Workload.breakdown.review + ea2Workload.breakdown.review,
          other: edWorkload.breakdown.other + ea1Workload.breakdown.other + ea2Workload.breakdown.other
        },
        recommendation: `🚨 NOT RECOMMENDED - ${Math.round(dailyHours)} hrs/day leaves no buffer. Defer ${eaSchool2.name || eaSchool2.college_name} to Regular Decision.`
      });
    }

    return scenarios;
  }

  /**
   * Generate daily sprint breakdown
   * @param {Object} scenario - Workload scenario
   * @param {number} daysUntil - Days until deadline
   * @returns {Array} - Array of daily sprints
   */
  static generateDailyBreakdown(scenario, daysUntil) {
    if (daysUntil <= 0) return [];

    const breakdown = [];
    const { breakdown: workloadBreakdown } = scenario;

    // Simplified daily breakdown logic
    // Priority: Essays → Forms → Financial Aid → Review

    const essayDays = Math.ceil((workloadBreakdown.essays / scenario.dailyHours) || 0);
    const formDays = Math.ceil((workloadBreakdown.forms / scenario.dailyHours) || 0);
    const faDays = Math.ceil((workloadBreakdown.financial_aid / scenario.dailyHours) || 0);
    const reviewDays = 2; // Always buffer 2 days for review
    const bufferDays = Math.max(1, daysUntil - essayDays - formDays - faDays - reviewDays);

    let currentDay = 1;

    if (essayDays > 0) {
      breakdown.push({
        days: `Day ${currentDay}-${currentDay + essayDays - 1}`,
        task: 'Complete all essays',
        hours: workloadBreakdown.essays
      });
      currentDay += essayDays;
    }

    if (formDays > 0) {
      breakdown.push({
        days: `Day ${currentDay}-${currentDay + formDays - 1}`,
        task: 'Common App + activities',
        hours: workloadBreakdown.forms
      });
      currentDay += formDays;
    }

    if (faDays > 0) {
      breakdown.push({
        days: `Day ${currentDay}-${currentDay + faDays - 1}`,
        task: 'FAFSA + CSS Profile',
        hours: workloadBreakdown.financial_aid
      });
      currentDay += faDays;
    }

    breakdown.push({
      days: `Day ${currentDay}-${currentDay + reviewDays - 1}`,
      task: 'Final review + parent review',
      hours: workloadBreakdown.review
    });
    currentDay += reviewDays;

    if (bufferDays > 0) {
      breakdown.push({
        days: `Day ${currentDay}`,
        task: 'Submit buffer day',
        hours: 0
      });
      currentDay++;
    }

    breakdown.push({
      days: `Day ${daysUntil}`,
      task: 'DEADLINE',
      hours: 0,
      isDeadline: true
    });

    return breakdown;
  }

  /**
   * Get tasks for today's sprint based on deadline phase
   * @param {Array} tasks - All tasks
   * @param {Object} phaseInfo - Current phase information
   * @param {number} maxHours - Maximum hours for today
   * @returns {Array} - Tasks for today's sprint
   */
  static getTodaySprintTasks(tasks, phaseInfo, maxHours = 4) {
    if (!phaseInfo || phaseInfo.phase === this.PHASES.NORMAL) {
      // Normal mode: just get next 3-4 tasks
      return tasks.slice(0, 4);
    }

    // Emergency/Sprint mode: filter by college and urgency
    const urgentTasks = tasks.filter(task => {
      // Must be for the urgent college
      if (task.college_id !== phaseInfo.college_id) return false;

      // Not yet completed
      if (task.completed || task.status === 'complete') return false;

      return true;
    });

    // Sort by priority and accumulated hours
    const sprintTasks = [];
    let totalHours = 0;

    for (const task of urgentTasks) {
      const taskHours = this.estimateTaskHours(task);
      if (totalHours + taskHours <= maxHours) {
        sprintTasks.push({
          ...task,
          estimated_hours: taskHours
        });
        totalHours += taskHours;
      }

      if (sprintTasks.length >= 5) break; // Max 5 tasks
    }

    return sprintTasks;
  }
}

export default DeadlineIntelligenceService;
